import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Search, FileText, ArrowUpRight } from "lucide-react";
import { Reveal, ChapterLabel } from "./Reveal";
import { LEAFLET_GROUPS } from "../data/content";

export const Leaflets = () => {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return LEAFLET_GROUPS;
    return LEAFLET_GROUPS.map((g) => ({
      ...g,
      items: g.items.filter(
        (item) =>
          item.title.toLowerCase().includes(q) || g.group.toLowerCase().includes(q)
      ),
    })).filter((g) => g.items.length > 0);
  }, [query]);

  return (
    <section
      id="leaflets"
      data-testid="leaflets-section"
      className="border-t border-[#E5DFD3] bg-[#1B3A5C]/[0.03]"
    >
      <div className="mx-auto max-w-6xl px-6 py-24 lg:px-10 lg:py-36">
        <Reveal>
          <ChapterLabel number="04" label="Patient Resources" />
          <h2
            data-testid="leaflets-title"
            className="font-display mt-6 text-3xl font-light tracking-tight text-[#1B3A5C] sm:text-4xl lg:text-5xl"
          >
            Patient information leaflets
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[#555]">
            Download and print your leaflet at home, or scan the QR code in clinic to return to
            this page any time.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative mt-12 max-w-md">
            <Search
              size={16}
              strokeWidth={1.5}
              className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 text-[#C9A66B]"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search leaflets, e.g. trabeculectomy"
              data-testid="leaflet-search-input"
              aria-label="Search patient leaflets"
              className="w-full border-b border-[#C9A66B]/40 bg-transparent py-3 pl-8 pr-4 font-display text-lg italic text-[#1B3A5C] placeholder:text-[#1B3A5C]/35 focus:border-[#C9A66B] focus:outline-none"
            />
          </div>
        </Reveal>

        <div className="mt-14 space-y-14">
          <AnimatePresence mode="popLayout">
            {filtered.map((group, gi) => (
              <motion.div
                key={group.group}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <h3
                  data-testid={`leaflet-group-${group.group.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex items-baseline gap-4 text-xs font-semibold uppercase tracking-[0.22em] text-[#C9A66B]"
                >
                  <span>{group.group}</span>
                  <span className="h-px flex-1 bg-[#C9A66B]/25" aria-hidden="true" />
                  <span className="font-display text-sm italic font-light normal-case tracking-normal text-[#1B3A5C]/50">
                    {group.items.length} {group.items.length === 1 ? "leaflet" : "leaflets"}
                  </span>
                </h3>

                <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                  {group.items.map((item, ii) => (
                    <li key={item.file}>
                      <a
                        href={`${process.env.PUBLIC_URL}/leaflets/${item.file}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`leaflet-link-${gi}-${ii}`}
                        className="group flex h-full items-center justify-between gap-4 border border-[#C9A66B]/25 bg-white px-6 py-5 transition-[border-color,transform] duration-400 hover:-translate-y-0.5 hover:border-[#C9A66B]/70"
                      >
                        <span className="flex items-center gap-4">
                          <FileText size={18} strokeWidth={1.25} className="shrink-0 text-[#C9A66B]" />
                          <span>
                            <span className="block text-[15px] font-medium leading-snug text-[#2D2D2D] transition-colors duration-300 group-hover:text-[#1B3A5C]">
                              {item.title}
                            </span>
                            <span className="mt-1 block text-[11px] uppercase tracking-[0.18em] text-[#C9A66B]">
                              PDF
                            </span>
                          </span>
                        </span>
                        <ArrowUpRight
                          size={16}
                          strokeWidth={1.5}
                          className="shrink-0 text-[#C9A66B] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                        />
                      </a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </AnimatePresence>

          {filtered.length === 0 && (
            <p
              data-testid="leaflet-search-empty"
              className="font-display text-lg italic text-[#1B3A5C]/60"
            >
              No leaflets match “{query}”. If you don't see the leaflet you're looking for,
              please contact your practice directly.
            </p>
          )}
        </div>

        <Reveal delay={0.1}>
          <p data-testid="leaflet-note" className="mt-14 text-[13.5px] italic text-[#888]">
            If you don't see the leaflet you're looking for, please contact the practice
            directly. New leaflets are added over time.
          </p>
          <p data-testid="leaflet-sources" className="mt-4 max-w-2xl text-[11.5px] leading-relaxed text-[#a5a5a5]">
            Leaflet sources: Glaucoma UK (glaucoma.uk); iStent infinite and Paul Glaucoma
            Implant brochures courtesy of Glaukos; Preserflo MicroShunt courtesy of Santen.
            These leaflets provide general information and do not replace personalised advice
            from your eye care team.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
